import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import FloatingNav from '../components/FloatingNav';
import { Send, CheckCircle, AlertCircle, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = ''; // Relative paths — works on any domain
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({ type: 'success', message: t('contact.successMessage') });
      setFormData({ name: '', company: '', email: '', phone: '', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: t('contact.errorMessage') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#0D0D0D]">
      {/* Floating Navigation */}
      <FloatingNav sections={[]} />

      {/* Hero Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left Content */}
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-wine mb-4 block">
                {t('nav.contact')}
              </span>
              <h1 
                data-testid="contact-title"
                className="font-heading text-4xl sm:text-5xl font-semibold text-white leading-tight mb-6"
              >
                {t('contact.title')}
              </h1>
              <p className="text-lg text-white/60 leading-relaxed mb-8">
                {t('contact.subtitle')}
              </p>

              {/* Privacy Badge */}
              <div className="flex items-center gap-3 p-4 bg-wine/10 rounded-lg border border-wine/20 mb-8">
                <Shield className="w-5 h-5 text-wine" strokeWidth={1.5} />
                <span className="text-white/70 text-sm">
                  {language === 'fr' 
                    ? 'Vos informations sont traitées avec la plus stricte confidentialité'
                    : 'Your information is handled with the strictest confidentiality'
                  }
                </span>
              </div>
              
              {/* Image */}
              <div className="hidden lg:block">
                <img
                  src="https://customer-assets.emergentagent.com/job_tap-platform-build/artifacts/nirmqqq2_578B4643-5F53-415B-8D2E-0D66AD3011A9.png"
                  alt="Restaurant interior"
                  className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
                />
              </div>
            </div>

            {/* Form */}
            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 lg:p-8 shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white/80 font-medium">
                    {t('contact.nameLabel')} *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.namePlaceholder')}
                    data-testid="contact-name-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:ring-wine focus:border-wine rounded-lg h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-white/80 font-medium">
                    {t('contact.companyLabel')} *
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t('contact.companyPlaceholder')}
                    data-testid="contact-company-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:ring-wine focus:border-wine rounded-lg h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/80 font-medium">
                    {t('contact.emailLabel')} *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.emailPlaceholder')}
                    data-testid="contact-email-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:ring-wine focus:border-wine rounded-lg h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white/80 font-medium">
                    {t('contact.phoneLabel')}
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('contact.phonePlaceholder')}
                    data-testid="contact-phone-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:ring-wine focus:border-wine rounded-lg h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-white/80 font-medium">
                    {t('contact.messageLabel')} *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.messagePlaceholder')}
                    data-testid="contact-message-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:ring-wine focus:border-wine rounded-lg resize-none"
                  />
                </div>

                {status.message && (
                  <div 
                    data-testid="contact-status"
                    className={`flex items-center gap-2 p-4 rounded-lg ${
                      status.type === 'success' 
                        ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                        : 'bg-red-500/10 text-red-400 border border-red-500/20'
                    }`}
                  >
                    {status.type === 'success' 
                      ? <CheckCircle className="w-5 h-5" strokeWidth={1.5} />
                      : <AlertCircle className="w-5 h-5" strokeWidth={1.5} />
                    }
                    <span>{status.message}</span>
                  </div>
                )}

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                  className="w-full bg-wine hover:bg-wine-light text-white rounded-lg h-12 text-base font-medium disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {language === 'fr' ? 'Envoi...' : 'Sending...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" strokeWidth={1.5} />
                      {t('contact.submitButton')}
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
